import { useEffect, useRef, useState } from 'react';
import { Socket } from 'socket.io-client';

/**
 * Listens for `call:transcript` events from the server and assembles the
 * full transcript for the current call session.
 *
 * Audio capture and streaming is handled by useWebRTC (MediaRecorder →
 * `audio_data_chunk` → backend → Whisper → `call:transcript`).
 *
 * Usage:
 *   const { isCapturing, fullTranscript } = useTranscription({
 *     remoteStream,
 *     socket,
 *     sessionId,
 *     enabled: callState === CallState.CONNECTED,
 *   });
 */

interface UseTranscriptionOptions {
  /** The remote MediaStream — used only to determine if audio is active. */
  remoteStream: MediaStream | null;
  /** The active Socket.IO socket used for signalling. */
  socket: Socket | null;
  /** WebRTC session ID used to correlate transcript events. */
  sessionId: string | null;
  /** Whether to listen for transcript events. */
  enabled?: boolean;
}

interface UseTranscriptionReturn {
  /** True while the call is active and remote audio is present. */
  isCapturing: boolean;
  /** Latest transcript segment received from the server. */
  latestTranscript: string | null;
  /** Full assembled transcript for this call. */
  fullTranscript: string;
}

export function useTranscription({
  remoteStream,
  socket,
  sessionId,
  enabled = true,
}: UseTranscriptionOptions): UseTranscriptionReturn {
  const [latestTranscript, setLatestTranscript] = useState<string | null>(null);
  const [fullTranscript, setFullTranscript] = useState('');

  // isCapturing: true while the call is active and remote audio is present
  const isCapturing =
    enabled &&
    remoteStream !== null &&
    remoteStream.getAudioTracks().length > 0;

  // Keep the last ACTIVE session ID in a ref so we can still match the
  // call:transcript event that arrives from Whisper *after* the call ends
  // (endCall sets sessionId → null before Whisper finishes).
  const lastSessionIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (sessionId !== null) {
      // New call started — update tracked ID and reset transcript state
      lastSessionIdRef.current = sessionId;
      setLatestTranscript(null);
      setFullTranscript('');
    }
    // When sessionId goes null (call ended) we intentionally leave
    // lastSessionIdRef.current pointing at the just-ended session so the
    // incoming Whisper result can still be matched and displayed.
  }, [sessionId]);

  // ── Listen for transcript events from the server ──────────────────────────

  useEffect(() => {
    if (!socket) return;

    const handler = (data: { session_id: string; transcript: string; status: string }) => {
      // Accept events for the current OR the most-recently-ended session
      if (data.session_id !== lastSessionIdRef.current) return;
      if (data.transcript) {
        setLatestTranscript(data.transcript);
        setFullTranscript(prev => (prev ? `${prev}\n${data.transcript}` : data.transcript));
      }
    };

    socket.on('call:transcript', handler);
    return () => { socket.off('call:transcript', handler); };
  }, [socket]); // sessionId intentionally excluded — we read lastSessionIdRef instead

  return { isCapturing, latestTranscript, fullTranscript };
}
